import { z } from 'zod';
/**
 * THE OUTBOUND EMAIL SEAM — the message shape, and nothing about a vendor.
 *
 * DECISION.md §2.12 is the binding text and it is short: "send through one
 * internal `sendEmail(template, locale, to, data)` function, with templates as
 * MJML or plain HTML **in your repo, never in the vendor's template editor**.
 * Email providers are the single most-swapped dependency in a SaaS; this makes
 * the vendor a 20-line adapter." §7.4 item 11 repeats it as a build rule:
 * "Abstract email behind one `sendEmail()` interface, with templates in the
 * repo. Provider swaps are near-certain."
 *
 * So this file is deliberately the whole vendor-facing vocabulary, and it
 * names no vendor. `EmailSender` (apps/api/src/email/sender/email-sender.ts)
 * consumes these types; `MockSender` and `ResendSender` implement it; nothing
 * else in the product knows an email provider exists. It is the same shape the
 * e-invoicing signer seam took (DECISION.md §8.10) and for the same reason.
 *
 * FOUR PROPERTIES ARE LOAD-BEARING, each written into the types below:
 *
 * 1. A message is a VALUE, fully rendered. The subject and the body text are
 *    on the message, not a template name the adapter looks up — an adapter can
 *    never reach into the database, can never render differently from what was
 *    recorded, and can be unit-tested with a literal.
 * 2. A message carries the RECIPIENT'S language, not the sender's. It is a
 *    required field, so there is no path that forgets to decide.
 * 3. `EmailSendResult` distinguishes RETRYABLE from permanent failure. A
 *    provider outage must be retried; a malformed address must not be retried
 *    forever. A boolean `ok` alone would lose that, and losing it means either
 *    infinite retries or silently dropped mail.
 * 4. Text first. §2.12 warns that DMARC-noncompliant senders see 22–34% go to
 *    spam; a wall of broken HTML in Arabic is the same failure by a different
 *    route. Every template renders plain text, and the shape has no HTML field
 *    to fill in badly.
 */
// ── Language ────────────────────────────────────────────────────────────────
/** The product's two languages (PART 3). The RECIPIENT's, never the sender's. */
export const EmailLanguageSchema = z.enum(['en', 'ar']);
// ── Templates ───────────────────────────────────────────────────────────────
/**
 * Every template the product can send, and no others. A closed list because
 * the alternative — free-text template names — makes "which mails does this
 * system send?" an unanswerable question, and that question is asked in every
 * security review.
 *
 * Each name maps to one renderer per language in
 * apps/api/src/email/templates/. Adding a template is a new entry here, two
 * renderers, and nothing else.
 */
export const EMAIL_TEMPLATES = [
    /** ADM-05 §15.2 — an administrator invites a colleague (BR-ADM-05-16). */
    'user_invitation',
    /** OPS-01 — the operator creates a company; its Owner is invited (BR-OPS-01-03). */
    'owner_invitation',
    /** GEN-S1 "Forgot password?" — the one-time reset link. */
    'password_reset',
    /** GEN-S2 — the activation link at signup (BR-GEN-S2-08/09). */
    'email_verification',
    /** CRM-04 "Send Statement" — the statement to a customer or supplier. */
    'partner_statement',
    /**
     * PART 22 §22.5 E / BR-OPS-02-08: the passphrase for a tenant data export,
     * addressed to the tenant's OWNER — never to the operator, and never in the
     * same place as the archive. The archive is downloaded from the console; the
     * key arrives by mail. That split IS the rule.
     */
    'tenant_export_passphrase',
    /**
     * PUR-01 "Send to supplier" — wave 2 of specs/erp-gap-analysis.md. The
     * order's own PDF rides as the message's one attachment (`documentId` on
     * the outbox row; `attachments` on the message). D15's head for it is the
     * statement's: a document a user explicitly sends to the party it names,
     * by a button, never a notification — see the census for the argument.
     */
    'purchase_order',
];
/**
 * ── THE D15 FENCE, AND WHY THIS LIST IS SHORTER THAN IT WAS ────────────────
 *
 * Retired by delegated ruling D15 (26 Aug 2026): "notifications → in-app for
 * everything; email only for invitations, password resets, and statements a
 * user explicitly sends."
 *
 * A seventh template, `check_reminder`, used to sit above. ADM-04 §12 gave
 * cheque reminders a delivery setting (`checks.reminder_method` — Email /
 * In-app / Both) and the notification wave honoured it, which made the cheque
 * reminder the one notification kind that could leave the product by mail.
 * D15 removes that case: cheque reminders are in-app, like everything else.
 *
 * WHAT THE REMAINING SIX ARE, sorted by the ruling's own heads, because a
 * closed list is only a fence if somebody can check each member against the
 * rule that admits it:
 *
 *   invitations                 — `user_invitation`, `owner_invitation`
 *   password resets             — `password_reset`
 *   statements explicitly sent  — `partner_statement`
 *
 * and two that D15 does not speak to, because D15 governs NOTIFICATION
 * DELIVERY and neither of these is a notification:
 *
 *   `email_verification`        — GEN-S2's signup activation link
 *                                 (BR-GEN-S2-08/09). An account-credential
 *                                 mail, the same class as a reset.
 *   `tenant_export_passphrase`  — BR-OPS-02-08's separate channel. The whole
 *                                 rule is that the key does NOT travel with
 *                                 the archive, so removing the mail would
 *                                 remove the control.
 *
 * That reading was ruled by the orchestrator under the 26 Aug delegation
 * rather than assumed here. `apps/api/test/email-census.spec.ts` pins all six
 * with their heads AND pins the call sites that reach `EmailService.enqueue`,
 * so a seventh template or a fourth business caller is red rather than a
 * judgement call somebody makes alone at 2am.
 *
 * The settings row `checks.reminder_method` went too, in a follow-up wave
 * under the same ruling: with no email arm left for it to choose, it offered
 * one honest value, and a setting that cannot change anything is worse than
 * no setting. `org-settings.ts`'s Checks group holds the argument and says
 * what happens to rows tenants had already stored.
 */
export const EmailTemplateSchema = z.enum(EMAIL_TEMPLATES);
// ── The message ─────────────────────────────────────────────────────────────
/**
 * One file riding with a message — wave 2's purchase-order PDF. The bytes
 * travel BASE64 because every provider's JSON API takes them that way and a
 * message is a value an adapter can log, hash and replay; the dispatcher
 * encodes them off the `documents` row at send time, so the outbox never
 * holds a second copy.
 */
export const EmailAttachmentSchema = z
    .object({
    /** ASCII and boring, like `documentFilename`: it becomes a header value. */
    filename: z.string().min(1).max(120),
    contentType: z.string().min(1).max(120),
    /** Base64. */
    content: z.string().min(1),
})
    .strict();
/**
 * What an adapter is handed. Rendered, addressed, and complete.
 *
 * `subject` and `text` are rendered at ENQUEUE time, not at send time, and
 * that is deliberate: the row in `email_outbox` then records exactly what was
 * going to go out, so "what did we send that customer?" is answerable, and a
 * template edit tomorrow does not retroactively change a message queued today.
 */
export const EmailMessageSchema = z
    .object({
    /** Which template produced this. Recorded, and used for nothing else. */
    template: EmailTemplateSchema,
    /** One recipient. Bulk sending is not a thing this seam does (§2.12: one-click unsubscribe is required on anything bulk). */
    to: z.string().max(320),
    /** The display name, when one is known. */
    toName: z.string().max(200).nullable(),
    language: EmailLanguageSchema,
    subject: z.string().min(1).max(300),
    /** Plain text, UTF-8. Arabic bodies are RTL text, not HTML that hopes. */
    text: z.string().min(1),
    /** Wave 2 — at most a handful of files; today exactly one, the purchase order's PDF. Absent on every other template. */
    attachments: EmailAttachmentSchema.array().max(5).optional(),
})
    .strict();
// ── Delivery record ─────────────────────────────────────────────────────────
/**
 * The life of one queued message. Mirrors `email_outbox.status`.
 *
 * `failed` is terminal and deliberately visible: DECISION.md §2.12's failure
 * mode is invitations that never arrive with nobody noticing, so a message
 * that has exhausted its attempts stops being pending and starts being a
 * row somebody can find.
 */
export const EmailStatusSchema = z.enum(['pending', 'sent', 'failed']);
// ── The sentences this seam says ────────────────────────────────────────────
/**
 * Fixed wording, in the contract like every other module's, so the API and the
 * two web dictionaries cannot drift.
 */
export const EMAIL_MESSAGES = {
    /**
     * The registry's refusal. Named after the signer's
     * `jurisdictionNotImplemented`, and for the identical reason: silently
     * falling back to the mock in production means invitations that never
     * arrive and nobody noticing.
     */
    providerNotImplemented: (provider) => `No email provider named "${provider}" is built. Set EMAIL_PROVIDER to one that is, or mail will not be sent.`,
    /** A known provider whose credential is absent. Also a refusal, never a fallback. */
    providerNotConfigured: (provider) => `The ${provider} email provider is selected but not configured. Its API key is missing from the environment.`,
    /** CRM-04 §5 — the green confirmation, which names the address. */
    statementEmailed: (address) => `Statement emailed to ${address}`,
    /** CRM-04 — a partner with no address on file cannot be sent anything. */
    partnerHasNoEmail: (name) => `${name} has no email address on file. Add one before sending a statement.`,
    /**
     * GEN-S1 "Forgot password?". The SAME sentence whether or not the address
     * exists — DECISION.md §5.16: "a generic 200 always", because any other
     * answer is an account-existence oracle.
     */
    resetRequested: 'If that email address has an account, a reset link is on its way. Check your inbox.',
    /** The reset link, opened after its 15 minutes or after it was used. */
    resetLinkInvalid: 'That reset link has expired or has already been used. Ask for a new one.',
    /** Too many reset requests — 5/hr per connection, 3/hr per address (§5.16). */
    resetRateLimited: 'Too many reset requests. Try again later.',
    /** GEN-S2 — the activation link, opened after its 72 hours (BR-GEN-S2-09). */
    verificationLinkInvalid: 'That activation link has expired. Ask for a new one.',
    /** The activation link, opened a second time. Not an error worth alarming anyone. */
    alreadyVerified: 'This email address is already confirmed. You can sign in.',
    /** BR-GEN-S2-08, when REQUIRE_EMAIL_VERIFICATION is on. */
    emailNotVerified: 'Confirm your email address first. Check your inbox for the activation link.',
};
// ── Request/response shapes used by the routes that trigger a send ──────────
export const ForgotPasswordRequestSchema = z
    .object({
    email: z
        .string()
        .max(320)
        .transform((value) => value.trim().toLowerCase()),
    /** The recipient chose it on the screen they are standing on. */
    language: EmailLanguageSchema.default('en'),
})
    .strict();
export const ResetPasswordRequestSchema = z
    .object({
    /** `<verificationId>.<secret>` — the same token shape the invite uses. */
    token: z.string().min(3).max(200),
    password: z
        .string()
        .transform((value) => value.slice(0, 200))
        .pipe(z
        .string()
        .min(10, 'Password must be at least 10 characters.')
        .regex(/[\p{L}]/u, 'Password must contain at least one letter.')
        .regex(/[0-9]/, 'Password must contain at least one number.')),
})
    .strict();
//# sourceMappingURL=email.js.map